import { useEffect, useRef, useState } from 'react';
import { PixiSlotScene } from './pixiScene.js';
import starUrl from '../assets/gecko-terminal-button.png';

export default function SlotMachine({ state, transactionCounter, spinning, mode, autoMode = false, onSpin, onModeChange, onAutoModeChange, onLeverEnergyChange, leverBroken = false }) {
  const [attempt, setAttempt] = useState({ id: 0, compatible: false });
  const [graphics, setGraphics] = useState({ status: 'loading', detail: '' });
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const latest = useRef({ state, transactionCounter, spinning, mode, autoMode, leverBroken, onSpin, onModeChange, onAutoModeChange, onLeverEnergyChange });
  latest.current = { state, transactionCounter, spinning, mode, autoMode, leverBroken, onSpin, onModeChange, onAutoModeChange, onLeverEnergyChange };

  useEffect(() => {
    let cancelled = false;
    let localScene = null;

    let failed = false;
    setGraphics({ status: 'loading', detail: '' });
    function fail(error) {
      if (cancelled || failed) return;
      failed = true;
      const stage = localScene?.stageName || 'renderer';
      console.error('StarTax graphics:', stage, error);
      localScene?.destroy();
      sceneRef.current = null;
      if (!attempt.compatible) setAttempt(previous => ({ id: previous.id + 1, compatible: true }));
      else setGraphics({ status: 'error', detail: stage + ': ' + String(error?.message || error).slice(0, 180) });
    }
    async function boot() {
      if (!mountRef.current) return;

      const scene = new PixiSlotScene({
        host: mountRef.current,
        starUrl,
        onSpin,
        onModeChange,
        onLeverEnergyChange,
        onAutoModeChange,
        mode,
        autoMode,
      });

      localScene = scene;


      await scene.init({ compatible: attempt.compatible, onFailure: fail });

      if (cancelled || failed) {
        scene.destroy();
        return;
      }

      sceneRef.current = scene;
      scene.setSpinHandler(latest.current.onSpin);
      scene.setModeHandler(latest.current.onModeChange);
      scene.setAutoModeHandler(latest.current.onAutoModeChange);
      scene.setLeverEnergyHandler(latest.current.onLeverEnergyChange);
      scene.renderState(latest.current.state);
      scene.updateLiveCounters(latest.current.transactionCounter);
      scene.setMode(latest.current.mode);
      scene.setSpinning(latest.current.spinning);
      scene.setAutoMode(latest.current.autoMode);
      scene.setLeverBroken(latest.current.leverBroken);
      setGraphics({ status: "ready", detail: "" });
    }

    boot().catch(fail);

    return () => {
      cancelled = true;
      if (localScene) localScene.destroy();
      if (sceneRef.current === localScene) sceneRef.current = null;
    };
    // Recreate only after a graphics failure or an explicit retry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  useEffect(() => {
    sceneRef.current?.setSpinHandler(onSpin);
  }, [onSpin]);

  useEffect(() => {
    sceneRef.current?.setModeHandler(onModeChange);
  }, [onModeChange]);

  useEffect(() => {
    sceneRef.current?.setAutoModeHandler?.(onAutoModeChange);
  }, [onAutoModeChange]);

  useEffect(() => {
    sceneRef.current?.setLeverEnergyHandler?.(onLeverEnergyChange);
  }, [onLeverEnergyChange]);

  useEffect(() => {
    sceneRef.current?.renderState(state);
  }, [state]);

  useEffect(() => {
    sceneRef.current?.updateLiveCounters(transactionCounter);
  }, [state, transactionCounter]);

  useEffect(() => {
    sceneRef.current?.setSpinning(spinning);
  }, [spinning]);

  useEffect(() => {
    sceneRef.current?.setMode(mode);
  }, [mode]);

  useEffect(() => {
    sceneRef.current?.setAutoMode?.(autoMode);
  }, [autoMode]);

  useEffect(() => {
    sceneRef.current?.setLeverBroken?.(leverBroken);
  }, [leverBroken]);

  return <>
    <div ref={mountRef} className="pixiMount" aria-busy={graphics.status === 'loading'} />
    {graphics.status !== 'ready' && <div className="slotGraphicsStatus" role="status">
      <strong>{graphics.status === 'loading' ? (attempt.compatible ? 'Restoring reels…' : 'Loading reels…') : 'Reels could not start'}</strong>
      {graphics.status === 'error' && <>
        <p>The graphics renderer is unavailable. You can retry without reloading the page.</p>
        <button type="button" onClick={() => setAttempt(previous => ({ id: previous.id + 1, compatible: true }))}>Retry graphics</button>
        <details><summary>Graphics diagnostics</summary><code>{graphics.detail}</code></details>
      </>}
    </div>}
  </>;
}
